import styled from "@emotion/styled";
import { translate, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { Checkbox } from "@kaltura/ds-react-components";
import { ChangeEvent } from "react";

export interface ResultsCountProps {
    /**
     * total number of results on server
     */
    totalCount: number;

    /**
     * number of selected items
     */
    selectedCount?: number;

    /**
     * specific search-in label
     */
    searchInLabel?: string;

    /**
     * callback for checkbox change event
     * @param checked true if checkbox is now checked
     */
    onChange: (checked: boolean) => void;

    /**
     * checkbox value
     */
    selectAllValue: boolean;
}

const StyledResultsCount = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(4),
    fontSize: theme.typography.fontSize,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const StyledSelectCount = styled("span")(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
}));

const Separator = styled("div")(({ theme }) => ({
    borderRadius: "50%",
    backgroundColor: theme.kaltura.palette.tone1,
    width: 5,
    height: 5,
}));

/**
 * my-media results count
 */
export function ResultsCount({
    totalCount,
    selectedCount,
    searchInLabel,
    onChange,
    selectAllValue,
}: ResultsCountProps) {
    const handleCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.checked);
    };

    return (
        <StyledResultsCount className="kms-ds-my-media-results-count">
            <Checkbox
                onChange={handleCheckboxClick}
                value={selectAllValue}
                checked={selectAllValue}
                slotProps={{
                    input: {
                        "aria-label": translate(
                            "Checkbox for select all %1",
                            [selectAllValue ? "Checked" : "Unchecked"]
                        ),
                    },
                }}
            />
            {!searchInLabel && translatePlural("%1 Result found", "%1 Results found", [totalCount])}
            {searchInLabel
                && translatePlural("%1 Result found in %2", "%1 Results found in %2", [totalCount, searchInLabel])}
            {!!selectedCount && <Separator />}
            {!!selectedCount && <StyledSelectCount>{translate("%1 Selected", [selectedCount])}</StyledSelectCount>}
        </StyledResultsCount>
    );
}
